import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FolderIcon from './FolderIcon';
import { IconSearch, IconFile } from './icons';
import { formatBytes } from '../utils/crypto';

// Translucent flyout (same family as DockMenu/FamilyFlyout) with a real,
// working search over every file and folder the user owns — not scoped to
// the current folder. Filenames are already decrypted client-side by the
// time they reach here (see useFiles.searchAllFiles), so this is a plain
// substring filter, no server-side search possible in a zero-knowledge app.
export default function SearchFlyout({ open, onClose, onSearch, onOpenFolder, onOpenFile }) {
  const { t } = useTranslation('widgets');
  const [query, setQuery] = useState('');
  const [data, setData] = useState(null); // { files, folders }
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) { setQuery(''); setData(null); return; }
    setLoading(true);
    onSearch().then(setData).finally(() => setLoading(false));
    const focusTimer = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(focusTimer);
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const q = query.trim().toLowerCase();
  const folderById = new Map((data?.folders || []).map(f => [f.id, f]));
  function folderPathLabel(folderId) {
    const names = [];
    let cur = folderById.get(folderId);
    while (cur) { names.unshift(cur.name); cur = folderById.get(cur.parentId); }
    return names.join(' / ') || t('search_root_label');
  }

  const matchedFolders = q ? (data?.folders || []).filter(f => f.name.toLowerCase().includes(q)) : [];
  const matchedFiles   = q ? (data?.files || []).filter(f => (f.filename || '').toLowerCase().includes(q)) : [];
  const hasResults = matchedFolders.length > 0 || matchedFiles.length > 0;

  return (
    <>
      <div className="dockmenu-backdrop" onClick={onClose} />
      <div className="dockmenu searchflyout" onClick={e => e.stopPropagation()}>
        <div className="search-input-wrap">
          <IconSearch width={16} height={16} />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('search_placeholder')}
          />
        </div>
        <div className="search-results">
          {loading && q && <div className="search-empty">{t('search_loading')}</div>}
          {!loading && q && !hasResults && <div className="search-empty">{t('search_no_results', { query })}</div>}

          {matchedFolders.map(f => (
            <button key={`folder-${f.id}`} className="search-result" onClick={() => { onOpenFolder(f.id); onClose(); }}>
              <FolderIcon name={f.name} size={28} />
              <div className="search-result-info">
                <span className="search-result-name">{f.name}</span>
                <span className="search-result-path">{t('search_in_folder', { path: folderPathLabel(f.parentId) })}</span>
              </div>
            </button>
          ))}

          {matchedFiles.map(f => (
            <button key={`file-${f.id}`} className="search-result" onClick={() => { onOpenFile(f); onClose(); }}>
              <span className="search-result-file-icon"><IconFile width={17} height={17} /></span>
              <div className="search-result-info">
                <span className="search-result-name">{f.filename}</span>
                <span className="search-result-path">{t('search_in_folder_size', { path: folderPathLabel(f.folderId), size: formatBytes(f.size) })}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
